import React from 'react';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useBuilderContext, useCampaignFormContext, useField } from '../../context';
import { useCampaignCalendars } from '../../hooks';
import { fieldBottomGapSx } from '../../helpers';
import dayjs from 'dayjs';

/**
 * Calendar picker: options sorted by name, archived or already-claimed calendars
 * rendered but disabled with an "(archived)" suffix. This control stays enabled
 * even when the campaign is read-only.
 */
export const AppointmentTypeField: React.FC = () => {
  const form = useCampaignFormContext();
  const { value, onChange } = useField('appointmentTypeId');
  const { onChange: onNameChange } = useField('name');
  const { campaignId, isDuplicate } = useBuilderContext();
  const selectCalendar = (id: string, name: string) => {
    onChange(id);
    onNameChange(`${dayjs().format('YYYY-MM-DD')} - ${name} Calendar Event Reminder`);
  };
  const calendars = useCampaignCalendars({
    // A duplicate has no campaignId because it saves as a new campaign, but its
    // source calendar is already loaded. Treat it like an existing campaign for
    // auto-selection so a sole calendar cannot replace that source selection.
    isExistingCampaign: !!campaignId || !!isDuplicate,
    // Only the calendar is auto-selected; the default campaign name stays intact
    // until the user changes Calendar deliberately.
    onAutoSelect: id => {
      // Calendar loading is asynchronous. Treat this product default as part of
      // the initial form, while preserving anything the user changed meanwhile.
      if (!form.getValues().appointmentTypeId) form.setInitialField('appointmentTypeId', id);
    },
  });

  return (
    <FormControl sx={fieldBottomGapSx} fullWidth>
      <InputLabel>Calendar</InputLabel>
      <Select
        label="Calendar"
        MenuProps={{ disableScrollLock: true }}
        value={value || ''}
        onChange={(e: SelectChangeEvent) => {
          const selected = calendars.find(calendar => calendar.id === e.target.value);
          if (selected) selectCalendar(selected.id, selected.name);
        }}
        name="appointmentTypeId"
      >
        {[...calendars]
          .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
          .map(c => (
            <MenuItem value={c.id} key={c.id} disabled={c.status !== 'active' || !!c.campaignId}>
              {c.name} {c.status === 'active' ? '' : '(archived)'}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
